import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';

import { $api } from '@shared/api';
import { routes } from '@shared/api/routes';
import { keysToCamel } from '@shared/helpers';

import { equipmentInventoryQueryKeys } from '../model';

import type {
    FullInventory,
    FullInventoryDto,
    InventoryLogItem,
    InventoryLogItemDto,
    InventoryLogs,
    InventoryLogsDto,
    LoadInventoryLogsParams,
    PartiallyMoveInventoryPayload,
    ShortInventory,
    ShortInventoryDto,
    UpdateInventoryFreeDatePayload,
    UpdateInventoryPayload,
    UpdateInventoryStatusPayload,
    UploadInventoryPhotosPayload,
    WarehouseIdPayload,
} from '../model';

interface EquipmentInventoryApiResponse<T> {
    response: T;
    errors?: Record<string, unknown>;
}

const jsonHeaders = {
    'Content-Type': 'application/json;charset=utf-8',
};

const WAREHOUSE_ID_ROUTE_PARAM = '_warehouse_id_';
const COUNT_PER_PAGE_FIELD = 'count_per_page';

const buildQueryString = (params: Record<string, string | number | undefined>): string => {
    const queryParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
        if (typeof value !== 'undefined') {
            queryParams.set(key, String(value));
        }
    });

    const queryString = queryParams.toString();

    return queryString ? `?${queryString}` : '';
};

export const equipmentInventoryApi = {
    viewInventory: ({ inventoryGuid, signal }: { inventoryGuid: string; signal?: AbortSignal }) =>
        $api.get<EquipmentInventoryApiResponse<FullInventoryDto>>(
            routes.viewInventory({ _guid_: inventoryGuid }),
            { signal }
        ),
    viewInventoryQueryOptions: ({ inventoryGuid }: { inventoryGuid: string }) =>
        queryOptions({
            queryKey: equipmentInventoryQueryKeys.details(inventoryGuid),
            queryFn: (meta) =>
                equipmentInventoryApi.viewInventory({ inventoryGuid, signal: meta.signal }),
            select: (data) => keysToCamel(data.data.response as unknown as FullInventory),
            enabled: Boolean(inventoryGuid),
        }),

    loadInventoryLogs: ({
        inventoryGuid,
        page = 1,
        countPerPage = 20,
        signal,
    }: LoadInventoryLogsParams & { signal?: AbortSignal }) =>
        $api.get<EquipmentInventoryApiResponse<InventoryLogsDto>>(
            `${routes.loadInventoryLogs({ _guid_: inventoryGuid })}${buildQueryString({
                page,
                [COUNT_PER_PAGE_FIELD]: countPerPage,
            })}`,
            { signal }
        ),
    loadInventoryLogsQueryOptions: ({
        inventoryGuid,
        page = 1,
        countPerPage = 20,
    }: LoadInventoryLogsParams) =>
        queryOptions({
            queryKey: equipmentInventoryQueryKeys.logs(inventoryGuid, page, countPerPage),
            queryFn: (meta) =>
                equipmentInventoryApi.loadInventoryLogs({
                    inventoryGuid,
                    page,
                    countPerPage,
                    signal: meta.signal,
                }),
            select: (data) => keysToCamel(data.data.response as unknown as InventoryLogs),
            enabled: Boolean(inventoryGuid),
        }),
    loadInventoryLogsInfiniteQueryOptions: ({
        inventoryGuid,
        countPerPage = 20,
    }: Omit<LoadInventoryLogsParams, 'page'>) =>
        infiniteQueryOptions({
            queryKey: equipmentInventoryQueryKeys.infiniteLogs(inventoryGuid, countPerPage),
            queryFn: ({ pageParam, signal }) =>
                equipmentInventoryApi.loadInventoryLogs({
                    inventoryGuid,
                    page: pageParam,
                    countPerPage,
                    signal,
                }),
            initialPageParam: 1,
            getNextPageParam: (lastPage) => {
                const { current_page: currentPage, page_count: pageCount } =
                    lastPage.data.response.pagination;

                return currentPage < pageCount ? currentPage + 1 : undefined;
            },
            select: (data) => ({
                ...data,
                pages: data.pages.map((page) =>
                    keysToCamel(page.data.response as unknown as InventoryLogs)
                ),
            }),
            enabled: Boolean(inventoryGuid),
        }),

    createInventory: ({ warehouseId }: WarehouseIdPayload) =>
        $api.post<EquipmentInventoryApiResponse<ShortInventoryDto>>(
            routes.createTool({ [WAREHOUSE_ID_ROUTE_PARAM]: warehouseId }),
            undefined,
            { headers: jsonHeaders }
        ),
    copyInventory: ({ inventoryGuid }: { inventoryGuid: string }) =>
        $api.post<EquipmentInventoryApiResponse<ShortInventoryDto>>(
            routes.copyInventory({ _guid_: inventoryGuid }),
            undefined,
            { headers: jsonHeaders }
        ),
    partiallyMoveInventory: ({
        inventoryGuid,
        toWarehouseId,
        sortIndex,
        quantity,
    }: PartiallyMoveInventoryPayload) =>
        $api.post<EquipmentInventoryApiResponse<ShortInventoryDto>>(
            routes.partiallyMoveInventory({ _guid_: inventoryGuid }),
            { toWarehouseId, sortIndex, quantity },
            { headers: jsonHeaders }
        ),
    removeInventory: ({ inventoryGuid }: { inventoryGuid: string }) =>
        $api.delete<EquipmentInventoryApiResponse<{ success: boolean }>>(
            routes.removeTool({ _guid_: inventoryGuid }),
            { headers: jsonHeaders }
        ),
    updateInventoryStatus: ({ inventoryGuid, status }: UpdateInventoryStatusPayload) =>
        $api.post<EquipmentInventoryApiResponse<InventoryLogItemDto | []>>(
            routes.updateToolStatus({ _guid_: inventoryGuid }),
            { status },
            { headers: jsonHeaders }
        ),
    updateInventory: ({ inventoryGuid, body }: UpdateInventoryPayload) =>
        $api.post<
            EquipmentInventoryApiResponse<
                | { success: boolean }
                | { cost: number }
                | { price: number }
                | { cost: number; logItem: InventoryLogItemDto }
            >
        >(routes.updateInventory({ _guid_: inventoryGuid }), body, {
            headers: body instanceof FormData ? undefined : jsonHeaders,
        }),
    updateInventoryFreeDate: ({ inventoryGuid, date }: UpdateInventoryFreeDatePayload) =>
        $api.post<EquipmentInventoryApiResponse<{ success: boolean }>>(
            routes.updateInventoryFreeDate({ _guid_: inventoryGuid }),
            { date },
            { headers: jsonHeaders }
        ),
    uploadInventoryPhotos: ({ inventoryGuid, files }: UploadInventoryPhotosPayload) => {
        const formData = new FormData();

        files.forEach((file) => formData.append('files[]', file));

        return $api.post<EquipmentInventoryApiResponse<InventoryLogItemDto>>(
            routes.uploadInventoryPhotos({ _guid_: inventoryGuid }),
            formData
        );
    },

    mapShortInventory: (inventory: ShortInventoryDto) =>
        keysToCamel(inventory as unknown as ShortInventory),
    mapInventoryLogItem: (logItem: InventoryLogItemDto) =>
        keysToCamel(logItem as unknown as InventoryLogItem),
};

export type InventoryLogsQueryCache = Awaited<
    ReturnType<typeof equipmentInventoryApi.loadInventoryLogs>
>;
